<template>
    <div>
        <b-datatable
            ref="grid"
            :key="dataTableKey"
            :columns="columns"
            :endpoint="listRoute"
            :hidden-columns="hiddenColumns"
            :initial-sort="initialSort"
            :initial-filters="initialFilters"
            class="p-0"
            has-filter-selector
            item-key="id"
            :grid-title="horibaSamplesProvider && horibaSamplesProvider.name ? horibaSamplesProvider.name : ''"
            notification
            :keep-filters="false"
        >
            <template #buttons>
                <div class="px-2">
                    <v-tooltip v-if="can('horiba-samples.download')" top transition="v-fab-transition">
                        <template #activator="{ on, attrs }">
                            <v-btn
                                :disabled="exportCsvDisabled"
                                color="primary"
                                icon
                                v-bind="attrs"
                                v-on="on"
                                @click="$root.goTo(exportCsvUrl())"
                            >
                                <v-icon>mdi-printer</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ t('quality-control::button.export_csv') }} ss</span>
                    </v-tooltip>
                </div>
            </template>

            <template #filters="{ filters }">
                <v-col cols="4">
                    <b-filter-date
                        v-model="filters.filters.filter_group_date"
                        range
                        :label="t('quality-control::filters.label.date')"
                        @clear="onCloseDateFilter(filters, 'filter_group_date')"
                        @input="setGroupDate"
                    ></b-filter-date>
                </v-col>
            </template>

            <template #[`item.low_passed`]="{ item }">
                <v-icon v-if="!parseInt(item.low_passed) || !parseInt(item.normal_passed)" color="red"
                    >mdi-alert-octagon-outline
                </v-icon>
            </template>

            <template #[`item.user.name`]="{ item }">
                <span>{{ item.user !== null ? item.user.name : t('default::label.na') }}</span>
            </template>

            <template #[`item.group_date`]="{ item }">
                {{ item.group_date ? moment(item.group_date).format($root.dateFormat) : '' }}
            </template>

            <template #[`item.low_id`]="{ item }">
                <div class="d-flex">
                    <div class="pr-2">
                        <span
                            :class="!!parseInt(item.low_passed) ? '' : 'red--text'"
                            class="link"
                            @click="openDialog(item.low_id, item.low_setting_name)"
                            >{{ item.low_setting_name }}</span
                        >
                    </div>
                    <div>
                        <div class="grid-status d-inline ml-2">
                            <span>
                                {{
                                    !!parseInt(item.low_passed)
                                        ? t('quality-control::grid.label.passed')
                                        : t('quality-control::grid.label.failed')
                                }}
                            </span>
                        </div>
                    </div>
                </div>
            </template>

            <template #[`item.normal_id`]="{ item }">
                <div class="d-flex">
                    <div class="pr-2">
                        <span
                            :class="!!parseInt(item.normal_passed) ? '' : 'red--text'"
                            class="link"
                            @click="openDialog(item.normal_id, item.normal_setting_name)"
                            >{{ item.normal_setting_name }}</span
                        >
                    </div>
                    <div>
                        <div class="grid-status d-inline ml-2">
                            <span>
                                {{
                                    !!parseInt(item.normal_passed)
                                        ? t('quality-control::grid.label.passed')
                                        : t('quality-control::grid.label.failed')
                                }}
                            </span>
                        </div>
                    </div>
                </div>
            </template>
            <template #[`item.actions`]="{ item }">
                <div class="table-actions">
                    <v-tooltip v-if="can('horiba-samples.index')" top transition="v-fab-transition">
                        <template #activator="{ on, attrs }">
                            <v-btn
                                color="disabled-color"
                                icon
                                v-bind="attrs"
                                v-on="on"
                                @click="$root.goTo(detailsUrl(item))"
                            >
                                <v-icon>mdi-eye</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ t('default::button.details') }}</span>
                    </v-tooltip>
                </div>
            </template>
        </b-datatable>
        <v-dialog v-if="detailsDialog" v-model="detailsDialog" max-width="950px" @input="closeDetailsDialog">
            <probe-data-table
                :headers="viewProbeHeaders"
                :items="viewProbeItems"
                :table-title="tableTitle"
                :table-sub-title="tableSubTitle"
                :is-passed="selectedBloodResult.is_passed"
            ></probe-data-table>
        </v-dialog>
    </div>
</template>

<script>
import ProbeDataTable from './ProbeDataTable.vue';

export default {
    name: 'HoribaSamplesGroupDataTable',
    components: { ProbeDataTable },
    props: {
        listRoute: {
            type: String,
            required: true,
        },
        initialFilters: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            dataTableKey: 0,
            initialSort: '-group_date',
            hiddenColumns: ['low_setting_name', 'normal_passed', 'normal_setting_name'],
            columns: [
                {
                    text: '',
                    value: 'low_passed',
                    width: 55,
                    align: 'center',
                    sortable: false,
                },
                {
                    text: t('quality-control::grid.label.operator'),
                    value: 'user.name',
                    width: 200,
                    sortable: true,
                },
                {
                    text: t('quality-control::grid.label.date'),
                    value: 'group_date',
                    width: 140,
                    sortable: true,
                },
                {
                    text: t('quality-control::grid.label.low_QC'),
                    value: 'low_id',
                    sortable: true,
                    width: 200,
                },
                {
                    text: '',
                    value: 'low_setting_name',
                    width: 140,
                    sortable: true,
                },
                {
                    text: t('quality-control::grid.label.normal_QC'),
                    value: 'normal_id',
                    sortable: true,
                    width: 200,
                },
                {
                    text: '',
                    value: 'normal_passed',
                    width: 140,
                    sortable: true,
                },
                {
                    text: '',
                    value: 'normal_setting_name',
                    width: 140,
                    sortable: true,
                },
                {
                    text: t('default::grid.label.actions'),
                    value: 'actions',
                    sortable: false,
                    fields: null,
                    width: 100,
                    align: 'end',
                },
            ],
            viewProbeHeaders: [
                {
                    text: t('quality-control::grid.label.parameter'),
                    value: 'parameter',
                    width: 80,
                    sortable: true,
                },
                {
                    text: t('quality-control::grid.label.unit'),
                    value: 'unit',
                    width: 80,
                    sortable: false,
                },
                {
                    text: t('quality-control::grid.label.control'),
                    value: 'control',
                    width: 80,
                    sortable: true,
                },
                {
                    text: t('quality-control::grid.label.tolerance'),
                    value: 'tolerance',
                    width: 80,
                    sortable: true,
                },
                {
                    text: t('quality-control::grid.label.probe'),
                    value: 'probe',
                    width: 80,
                    sortable: true,
                },
            ],
            detailsDialog: false,
            selectedBloodResult: {},
            groupDate: null,
        };
    },
    computed: {
        horibaSamples() {
            return this.$store.state.qualityControl.horiba_samples;
        },
        horibaSamplesProvider() {
            return this.horibaSamples.provider;
        },
        viewProbeItems() {
            let items = new Array();
            this.selectedBloodResult.sample_properties.forEach((item) => {
                const mapItem = new Object();
                if (item.horiba_setting) {
                    mapItem.parameter = item.horiba_setting.property;
                    mapItem.unit = item.horiba_setting.unit;
                    mapItem.control = item.horiba_setting.value;
                    mapItem.tolerance = item.horiba_setting.tolerance;
                    mapItem.probe = item.value;
                    mapItem.isPassed = item.is_passed;
                } else {
                    mapItem.parameter = item.property;
                    mapItem.unit = item.unit;
                    mapItem.control = null;
                    mapItem.tolerance = null;
                    mapItem.probe = item.value;
                    mapItem.isPassed = item.isPassed;
                    mapItem.min = item.min;
                    mapItem.max = item.max;
                }

                items.push(mapItem);
            });
            return items;
        },
        tableTitle() {
            return `${this.selectedBloodResult.setting_name} - ${this.selectedBloodResult.type} QC`;
        },
        tableSubTitle() {
            return `${
                this.selectedBloodResult.is_passed
                    ? t('quality-control::grid.label.passed')
                    : t('quality-control::grid.label.failed')
            } - ${this.selectedBloodResult.setting_name} (${moment(this.selectedBloodResult.created_at).format(
                this.$root.dateFormat
            )}) - ${this.selectedBloodResult.type} QC!`;
        },
        exportCsvDisabled() {
            return this.$store.state.data_table.items && !this.$store.state.data_table.items.length > 0;
        },
    },
    methods: {
        setGroupDate(value) {
            this.groupDate = value;
        },
        detailsUrl(item) {
            return route('horiba-samples.index', {
                provider_id: this.initialFilters.provider_id,
                created_at: item.group_date,
            });
        },
        onCloseDateFilter(filters, filter) {
            switch (filter) {
                case 'filter_group_date':
                    filters.filters.group_date = null;
                    filters.filters.filter_group_date = null;
                    this.groupDate = null;
                    break;
                default:
                    break;
            }
        },
        openDialog(id, name) {
            let url = name.startsWith('PIX') ? 'api.v1.pixcell-samples.show' : 'api.v1.horiba-samples.show';
            let fields = name.startsWith('PIX') ? '*' : '*,sample_properties.*,sample_properties.horiba_setting.*';
            let params = {
                fields: fields,
            };
            params[name.startsWith('PIX') ? 'pixcell_sample' : 'horiba_sample'] = id;
            axios({
                method: 'get',
                url: route(url, params),
            })
                .then((result) => {
                    this.selectedBloodResult = result.data.data;
                    this.detailsDialog = true;
                })
                .catch((error) => {
                    this.$root.notify('error', error.response.data.error);
                    this.detailsDialog = false;
                });
        },
        closeDetailsDialog() {
            this.detailsDialog = false;
            this.selectedBloodResult = {};
        },
        dateFilterConverter(val) {
            let dates = _.clone(val);
            if (!val) {
                return null;
            }
            if (val && val.length <= 1) {
                return `${val[0]}`;
            }
            if (moment(val[0]).isAfter(moment(val[1]))) {
                dates[0] = `gte:${val[1]}`;
                dates[1] = `lte:${val[0]}`;
            }
            if (moment(val[1]).isAfter(moment(val[0]))) {
                dates[0] = `gte:${val[0]}`;
                dates[1] = `lte:${val[1]}`;
            }
            return dates.join(',');
        },
        exportCsvUrl() {
            const options = {
                group_date: this.dateFilterConverter(this.groupDate),
                provider_id: this.initialFilters.provider_id,
                fields: 'user.name,low_id,low_passed,low_setting_name,normal_id,normal_passed,normal_setting_name,group_date',
            };
            if (!this.groupDate) delete options.group_date;
            return route('horiba-samples.download', options);
        },
    },
};
</script>

<style></style>
